import { PHONE_DIGITS_MIN_LENGTH } from '@/constants/phone';

/**
 * Formats a phone number string to match PHONE_NUMBER_MASK format (+1 (XXX) XXX-XXXX)
 * @param phoneNumber - Raw phone number string (may contain digits only or be partially formatted)
 * @returns Formatted phone number string in the format +1 (XXX) XXX-XXXX, or empty string if invalid
 */
export const formatPhoneNumber = (phoneNumber: string | null | undefined): string => {
  if (!phoneNumber) {
    return '';
  }

  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  if (!digitsOnly) {
    return '';
  }

  const hasCountryCode = digitsOnly.startsWith('1');
  const localDigits = hasCountryCode ? digitsOnly.slice(1, 11) : digitsOnly.slice(0, 10);

  if (localDigits.length <= 3) {
    return `+1 (${localDigits}`;
  }

  if (localDigits.length <= 6) {
    const areaCode = localDigits.slice(0, 3);
    const firstPart = localDigits.slice(3);
    return `+1 (${areaCode}) ${firstPart}`;
  }

  const areaCode = localDigits.slice(0, 3);
  const firstPart = localDigits.slice(3, 6);
  const secondPart = localDigits.slice(6, 10);
  return `+1 (${areaCode}) ${firstPart}-${secondPart}`;
};

/**
 * Converts a phone value (masked or unmasked) to server format (+1XXXXXXXXXX).
 * Strips all non-digits and prepends +.
 */
export const toServerPhoneNumber = (value: string | null | undefined): string =>
  value ? `+${value.replace(/\D/g, '')}` : '';

/**
 * Returns true when the value is effectively empty: no digits, only US country code,
 * or all digits are the same (mask placeholder when user clears the field).
 */
export const isPhoneValueEmpty = (value: string | null | undefined): boolean => {
  if (!value) return true;
  const digits = value.replace(/\D/g, '');
  if (!digits) return true;
  return false;
};

/**
 * True when the value has a full US national number (10 digits), either as
 * `+1` + 10 digits (11 digits total) or 10 digits alone (legacy / API shapes).
 *
 * Aligns with MPA: same 11-digit threshold as `PHONE_DIGITS_MIN_LENGTH` / SupportPad;
 * some MPA screens also use `US_PHONE_SERVER_LENGTH` (12) on the stored `+1…` string.
 */
export const isCompleteUsPhoneNumber = (value: string | null | undefined): boolean => {
  if (!value || isPhoneValueEmpty(value)) return false;
  const digits = value.replace(/\D/g, '');
  if (digits.length === PHONE_DIGITS_MIN_LENGTH && digits.startsWith('1')) return true;
  return false;
};
